/**
 * Category Engine — what "a gym flyer" or "a salon flyer" means.
 *
 * Each category defines the recommendation pools the variation engine draws
 * from (layouts, palette themes, typography themes, decoration packs), the
 * base terms the image query engine expands, and a few sample content sets
 * so generated specs carry realistic copy. Later, OpenAI will usually
 * supply the copy; the samples keep the factory self-sufficient until then.
 */

const category = (def) => ({
  layouts: ['classic-flyer', 'centered-poster', 'split-hero', 'offer-first'],
  ...def,
});

export const CATEGORIES = {
  restaurant: category({
    id: 'restaurant', name: 'Restaurant',
    paletteThemes: ['warm', 'vibrant', 'earthy'],
    typographyThemes: ['bold', 'friendly', 'elegant'],
    decorationPacks: ['organic-flow', 'soft-minimal', 'geo-pop'],
    imageTerms: ['burger', 'fries', 'pizza', 'chef', 'fast food', 'cafe interior', 'gourmet dish'],
    content: [
      { primaryHeading: 'Taste The Difference', secondaryHeading: 'Fresh. Local. Delicious.', body: 'Hand-crafted dishes made from locally sourced ingredients, served daily.', offer: '2-FOR-1 MAINS', cta: 'Book A Table', footer: 'Open daily 11am–11pm · yourrestaurant.com' },
      { primaryHeading: 'Grand Opening Week', secondaryHeading: 'Join us for a celebration', body: 'Be among the first to experience our new menu and get exclusive opening offers.', offer: '30% OFF', cta: 'Reserve Now', footer: '123 Main Street · (555) 010-1234' },
    ],
  }),
  'real-estate': category({
    id: 'real-estate', name: 'Real Estate',
    paletteThemes: ['corporate', 'luxury', 'midnight'],
    typographyThemes: ['elegant', 'modern'],
    decorationPacks: ['clean', 'soft-minimal'],
    imageTerms: ['modern house', 'apartment interior', 'city skyline', 'luxury villa', 'keys handover', 'living room'],
    content: [
      { primaryHeading: 'Your Dream Home Awaits', secondaryHeading: 'Premium listings, personal service', body: 'Browse hand-picked properties in the most sought-after neighborhoods.', offer: 'FREE VALUATION', cta: 'View Listings', footer: 'Licensed broker · dreamhomes.com' },
      { primaryHeading: 'Open House Saturday', secondaryHeading: '3 bed · 2 bath · garden', body: 'A rare opportunity in a quiet street minutes from downtown. Tour it this weekend.', offer: 'FROM $420K', cta: 'Schedule A Tour', footer: '(555) 010-9876 · openhouse.com' },
    ],
  }),
  medical: category({
    id: 'medical', name: 'Medical',
    paletteThemes: ['fresh', 'corporate', 'pastel'],
    typographyThemes: ['modern', 'friendly'],
    decorationPacks: ['clean', 'soft-minimal'],
    imageTerms: ['doctor consultation', 'clinic interior', 'stethoscope', 'nurse smiling', 'medical team', 'health checkup'],
    content: [
      { primaryHeading: 'Your Health Comes First', secondaryHeading: 'Compassionate care, modern medicine', body: 'Same-week appointments with board-certified physicians for the whole family.', offer: 'FREE CHECK-UP', cta: 'Book Appointment', footer: 'Mon–Sat 8am–6pm · cityclinic.com' },
      { primaryHeading: 'Flu Season Is Here', secondaryHeading: 'Protect yourself and your family', body: 'Walk-in vaccinations available every day, no appointment needed.', offer: 'WALK-INS WELCOME', cta: 'Find A Clinic', footer: 'cityclinic.com/flu · (555) 010-2468' },
    ],
  }),
  salon: category({
    id: 'salon', name: 'Salon',
    paletteThemes: ['pastel', 'luxury', 'warm'],
    typographyThemes: ['elegant', 'friendly'],
    decorationPacks: ['organic-flow', 'soft-minimal'],
    imageTerms: ['hair styling', 'salon interior', 'manicure', 'makeup artist', 'spa treatment', 'hair color'],
    content: [
      { primaryHeading: 'Look Stunning Today', secondaryHeading: 'Hair · Nails · Beauty', body: 'Award-winning stylists using premium products, right in your neighborhood.', offer: '20% OFF FIRST VISIT', cta: 'Book Your Slot', footer: 'glowsalon.com · @glowsalon' },
      { primaryHeading: 'Bridal Season Special', secondaryHeading: 'Packages for your big day', body: 'Complete bridal styling — trial session included with every package.', offer: 'PACKAGES FROM $199', cta: 'Get The Look', footer: '(555) 010-7777 · glowsalon.com' },
    ],
  }),
  gym: category({
    id: 'gym', name: 'Gym',
    paletteThemes: ['vibrant', 'midnight', 'warm'],
    typographyThemes: ['bold', 'modern'],
    decorationPacks: ['geo-pop', 'clean'],
    imageTerms: ['weight training', 'gym interior', 'personal trainer', 'running workout', 'fitness class', 'dumbbells'],
    content: [
      { primaryHeading: 'Stronger Every Day', secondaryHeading: 'No excuses. Just results.', body: 'State-of-the-art equipment, expert coaches, and classes for every level.', offer: 'FIRST MONTH FREE', cta: 'Join Now', footer: 'Open 24/7 · ironworksgym.com' },
      { primaryHeading: 'New Year, New You', secondaryHeading: 'Transformation challenge starts now', body: 'Eight weeks, a personal coach, and a community that keeps you accountable.', offer: '50% OFF SIGN-UP', cta: 'Start Today', footer: '(555) 010-3131 · ironworksgym.com' },
    ],
  }),
  travel: category({
    id: 'travel', name: 'Travel',
    paletteThemes: ['fresh', 'vibrant', 'midnight'],
    typographyThemes: ['modern', 'elegant'],
    decorationPacks: ['organic-flow', 'soft-minimal'],
    imageTerms: ['tropical beach', 'mountain landscape', 'airplane window', 'city travel', 'passport map', 'resort pool'],
    content: [
      { primaryHeading: 'Escape The Ordinary', secondaryHeading: 'Curated trips, unbeatable prices', body: 'Handpicked destinations with flights, stays, and experiences bundled for you.', offer: 'UP TO 40% OFF', cta: 'Explore Deals', footer: 'wanderly.com · ATOL protected' },
      { primaryHeading: 'Summer In Santorini', secondaryHeading: '7 nights from $899', body: 'Whitewashed villages, endless sunsets, and the bluest water you have ever seen.', offer: 'EARLY-BIRD PRICING', cta: 'Book The Trip', footer: '(555) 010-4545 · wanderly.com' },
    ],
  }),
  corporate: category({
    id: 'corporate', name: 'Corporate',
    paletteThemes: ['corporate', 'luxury', 'midnight'],
    typographyThemes: ['modern', 'elegant'],
    decorationPacks: ['clean', 'geo-pop'],
    imageTerms: ['business meeting', 'office skyline', 'handshake deal', 'team collaboration', 'conference room', 'laptop workspace'],
    content: [
      { primaryHeading: 'Annual Leadership Summit', secondaryHeading: 'Strategy. Innovation. Growth.', body: 'Two days of keynotes and workshops with industry leaders from around the world.', offer: 'EARLY-BIRD PASSES', cta: 'Register Now', footer: 'summit2026.com · Oct 14–15' },
      { primaryHeading: 'We Are Hiring', secondaryHeading: 'Build the future with us', body: 'Join a team that ships products used by millions. Hybrid-friendly roles open now.', offer: '12 OPEN ROLES', cta: 'See Positions', footer: 'careers.acme.com' },
    ],
  }),
  education: category({
    id: 'education', name: 'Education',
    paletteThemes: ['fresh', 'corporate', 'vibrant'],
    typographyThemes: ['friendly', 'modern'],
    decorationPacks: ['soft-minimal', 'geo-pop'],
    imageTerms: ['students classroom', 'graduation cap', 'online learning', 'library books', 'science lab', 'campus building'],
    content: [
      { primaryHeading: 'Enroll For Fall 2026', secondaryHeading: 'Programs that launch careers', body: 'Flexible schedules, expert faculty, and hands-on learning from day one.', offer: 'SCHOLARSHIPS OPEN', cta: 'Apply Today', footer: 'admissions@brightu.edu · brightu.edu' },
      { primaryHeading: 'Free Coding Bootcamp', secondaryHeading: 'Zero to developer in 12 weeks', body: 'Learn full-stack development with mentors from top tech companies.', offer: 'LIMITED SEATS', cta: 'Save My Seat', footer: 'codecamp.io/apply' },
    ],
  }),
  food: category({
    id: 'food', name: 'Food',
    paletteThemes: ['warm', 'earthy', 'vibrant'],
    typographyThemes: ['friendly', 'bold'],
    decorationPacks: ['organic-flow', 'soft-minimal'],
    imageTerms: ['fresh vegetables', 'homemade pasta', 'street food', 'dessert closeup', 'coffee latte art', 'farmers market'],
    content: [
      { primaryHeading: 'Fresh To Your Door', secondaryHeading: 'Meal kits made simple', body: 'Chef-designed recipes and pre-portioned ingredients delivered weekly.', offer: '3 MEALS FREE', cta: 'Get Started', footer: 'freshbox.com · cancel anytime' },
      { primaryHeading: 'Weekend Food Market', secondaryHeading: '40+ local vendors', body: 'Street food, craft coffee, fresh produce, and live music every Saturday.', offer: 'FREE ENTRY', cta: 'Plan Your Visit', footer: 'Riverside Park · Sat 9am–4pm' },
    ],
  }),
  events: category({
    id: 'events', name: 'Events',
    paletteThemes: ['midnight', 'vibrant', 'luxury'],
    typographyThemes: ['bold', 'elegant'],
    decorationPacks: ['geo-pop', 'organic-flow'],
    imageTerms: ['concert crowd', 'stage lights', 'dj party', 'wedding venue', 'fireworks night', 'festival tent'],
    content: [
      { primaryHeading: 'One Night Only', secondaryHeading: 'Live at the Grand Hall', body: 'An unforgettable evening of music, lights, and energy. Doors open at 8pm.', offer: 'TICKETS FROM $29', cta: 'Get Tickets', footer: 'grandhall.com/events · 18+' },
      { primaryHeading: 'New Year Gala 2027', secondaryHeading: 'Dinner · Dancing · Countdown', body: 'Ring in the new year with a five-course dinner and a live orchestra.', offer: 'VIP TABLES OPEN', cta: 'Reserve Seats', footer: 'gala.example.com · Dec 31' },
    ],
  }),
  festival: category({
    id: 'festival', name: 'Festival',
    paletteThemes: ['vibrant', 'warm', 'midnight'],
    typographyThemes: ['bold', 'friendly'],
    decorationPacks: ['geo-pop', 'organic-flow'],
    imageTerms: ['music festival', 'confetti celebration', 'lantern festival', 'summer festival crowd', 'carnival colors', 'holi colors'],
    content: [
      { primaryHeading: 'Summer Sound Festival', secondaryHeading: '3 days · 5 stages · 60 artists', body: 'Camp under the stars and dance until sunrise at the biggest festival of the year.', offer: 'WEEKEND PASSES', cta: 'Grab Your Pass', footer: 'summersound.com · June 19–21' },
      { primaryHeading: 'Festival Of Lights', secondaryHeading: 'A celebration for the whole family', body: 'Food stalls, live performances, and a lantern parade through the old town.', offer: 'FREE ADMISSION', cta: 'See The Program', footer: 'cityfest.org · Nov 7' },
    ],
  }),
  fashion: category({
    id: 'fashion', name: 'Fashion',
    paletteThemes: ['luxury', 'pastel', 'midnight'],
    typographyThemes: ['elegant', 'modern'],
    decorationPacks: ['clean', 'soft-minimal'],
    imageTerms: ['fashion model', 'clothing rack', 'street style', 'runway show', 'accessories flatlay', 'boutique interior'],
    content: [
      { primaryHeading: 'The New Collection', secondaryHeading: 'Autumn / Winter 2026', body: 'Tailored silhouettes and bold textures, designed to be lived in.', offer: 'MEMBERS PREVIEW', cta: 'Shop The Drop', footer: 'atelier-nova.com · free returns' },
      { primaryHeading: 'End Of Season Sale', secondaryHeading: 'Wardrobe icons, final call', body: 'Last chance on this season’s bestsellers before they are gone for good.', offer: 'UP TO 60% OFF', cta: 'Shop Sale', footer: 'In store & online · atelier-nova.com' },
    ],
  }),
};

export const listCategories = () =>
  Object.values(CATEGORIES).map(({ id, name }) => ({ id, name }));

export const getCategory = (id) => {
  const cat = CATEGORIES[id];
  if (!cat) throw new Error(`Unknown category "${id}"`);
  return cat;
};
